import { useTranslation } from 'react-i18next';
import { Button, Input } from '@/components/ui';
import { appTileLabel } from './coverageHelpers';
import type { RoleKeyCreateFormVM } from './useRoleKeyCreateForm';

/**
 * EAA-1817: frame 54 ("10-empty-and-loading.png") — the two dead-end states
 * the editor card can land in, plus a plain "chưa chọn" fallback. Each is a
 * standalone `.hz-card`-shaped panel, matching the design's stacked cards
 * rather than one combined message.
 */

export function AppNotApprovedEmptyState({
    appCode,
    appName,
    canManageApps,
    onOpenApp,
}: {
    appCode: string;
    appName: string;
    canManageApps: boolean;
    onOpenApp: () => void;
}) {
    const { t } = useTranslation();
    return (
        <section
            className="hz-card rc-empty-card"
            aria-label={t('console.roleCoverage.appNotApproved.ariaLabel', { appName })}
        >
            <span className="rc-app-tile" aria-hidden="true">
                {appTileLabel(appCode)}
            </span>
            <h3 className="rc-empty-title">
                {t('console.roleCoverage.appNotApproved.title', { appName })}
            </h3>
            <p className="rc-card-note">{t('console.roleCoverage.appNotApproved.description')}</p>
            {canManageApps && (
                // EAA-1817: raw <button> → kit Button; class names kept so existing CSS still applies.
                <Button className="portal-btn portal-btn-primary" onClick={onOpenApp}>
                    {t('console.roleCoverage.appNotApproved.openAppButton')}
                </Button>
            )}
        </section>
    );
}

export function NoRoleKeysEmptyState({
    canManage,
    form,
}: {
    canManage: boolean;
    form: RoleKeyCreateFormVM;
}) {
    const { t } = useTranslation();
    return (
        <section
            className="hz-card rc-empty-card"
            aria-label={t('console.roleCoverage.noRoleKeys.ariaLabel')}
        >
            <h3 className="rc-empty-title">{t('console.roleCoverage.noRoleKeys.title')}</h3>
            <p className="rc-card-note">{t('console.roleCoverage.noRoleKeys.description')}</p>

            {canManage && !form.open && (
                // EAA-1817: raw <button> → kit Button; class names kept so existing CSS still applies.
                <Button className="portal-btn portal-btn-primary" onClick={() => form.setOpen(true)}>
                    {t('console.roleCoverage.noRoleKeys.createButton')}
                </Button>
            )}

            {canManage && form.open && (
                <form className="portal-admin-form rc-role-key-form" onSubmit={form.onSubmit}>
                    <label>
                        <span className="portal-field-label">
                            {t('console.roleCoverage.noRoleKeys.form.keyLabel')}
                            <span className="portal-field-required" aria-hidden="true">
                                {' '}
                                *
                            </span>
                        </span>
                        <Input
                            placeholder={t('console.roleCoverage.noRoleKeys.form.keyPlaceholder')}
                            value={form.form.key}
                            onChange={(e) => form.setForm({ ...form.form, key: e.target.value })}
                            required
                        />
                    </label>
                    <label>
                        <span className="portal-field-label">
                            {t('console.roleCoverage.noRoleKeys.form.nameLabel')}
                            <span className="portal-field-required" aria-hidden="true">
                                {' '}
                                *
                            </span>
                        </span>
                        <Input
                            placeholder={t('console.roleCoverage.noRoleKeys.form.namePlaceholder')}
                            value={form.form.name}
                            onChange={(e) => form.setForm({ ...form.form, name: e.target.value })}
                            required
                        />
                    </label>
                    <label>
                        <span className="portal-field-label">
                            {t('console.roleCoverage.noRoleKeys.form.descriptionLabel')}{' '}
                            <span className="portal-field-optional">
                                ({t('console.roleCoverage.noRoleKeys.form.optionalLabel')})
                            </span>
                        </span>
                        <Input
                            value={form.form.description}
                            onChange={(e) =>
                                form.setForm({ ...form.form, description: e.target.value })
                            }
                        />
                    </label>

                    {form.error && <p className="portal-admin-error">{form.error}</p>}

                    <div className="rc-role-key-form-actions">
                        {/* EAA-1817: raw <button>s → kit Button; `htmlType` (not `type`,
                            which antd reserves for the visual variant) keeps the submit
                            wired to this <form>'s onSubmit. */}
                        <Button className="portal-btn" onClick={() => form.setOpen(false)}>
                            {t('common.cancel')}
                        </Button>
                        <Button
                            htmlType="submit"
                            className="portal-btn portal-btn-primary"
                            disabled={form.pending}
                        >
                            {form.pending
                                ? t('console.roleCoverage.noRoleKeys.form.submitPending')
                                : t('console.roleCoverage.noRoleKeys.createButton')}
                        </Button>
                    </div>
                </form>
            )}
        </section>
    );
}

export function NoAppSelectedEmptyState() {
    const { t } = useTranslation();
    return (
        <section
            className="hz-card rc-empty-card"
            aria-label={t('console.roleCoverage.noAppSelected.ariaLabel')}
        >
            <p className="portal-admin-empty">
                {t('console.roleCoverage.noAppSelected.description')}
            </p>
        </section>
    );
}
